import React from "react";
import { Link } from "react-router-dom";
import { Moon } from "lucide-react";
import StarField from "../components/StarField";
import { TEAM } from "../mock/mockData";

const TeamPage = () => {
  return (
    <div className="relative" data-testid="team-page">
      {/* Header */}
      <section className="relative py-28 md:py-36 overflow-hidden">
        <StarField count={50} />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,165,116,0.12),transparent_60%)]" />
        <div className="container-lux relative z-10 max-w-4xl fade-up">
          <div className="divider-gold mb-6">The People Behind It</div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] mb-8">
            Real people. <em className="text-gold not-italic">Real care.</em>
          </h1>
          <p className="text-lg text-white/70 max-w-2xl leading-relaxed">
            Our team is hand-picked for empathy, experience, and the rare ability to make every moment feel a little lighter. Meet the people who'll walk beside you.
          </p>
        </div>
      </section>

      {/* Team Grid */}
      <section className="py-20 md:py-28" data-testid="team-grid">
        <div className="container-lux grid md:grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {TEAM.map((member, i) => (
            <div
              key={member.id}
              className="lux-card rounded-[2rem] overflow-hidden group flex flex-col"
              data-testid={`team-member-${member.id}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover object-top transition-transform duration-[1.2s] group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-deep via-deep/30 to-transparent" />
                <div className="absolute bottom-6 left-8 right-8">
                  <div className="text-xs tracking-[0.25em] uppercase text-gold mb-2">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-serif text-4xl text-white leading-tight">{member.name}</h3>
                  <div className="text-sm text-white/70 italic mt-1">{member.role}</div>
                </div>
              </div>
              <div className="p-8 md:p-10 flex flex-col flex-1">
                <p className="text-white/75 leading-relaxed mb-4">
                  {member.bio}
                </p>
                {member.bioExtra && (
                  <p className="text-white/60 leading-relaxed text-sm mb-6 italic">
                    {member.bioExtra}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 mt-auto pt-5 border-t border-white/5">
                  {member.specialties.map((sp) => (
                    <span
                      key={sp}
                      className="text-[10px] tracking-[0.15em] uppercase px-3 py-1.5 rounded-full bg-gold/10 border border-gold/20 text-gold"
                    >
                      {sp}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Join CTA */}
      <section className="py-28 md:py-32" data-testid="team-cta">
        <div className="container-lux">
          <div className="lux-card rounded-[2rem] p-12 md:p-16 text-center max-w-4xl mx-auto relative overflow-hidden">
            <Moon className="w-10 h-10 text-gold mx-auto mb-6 moon-glow" />
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              Want to join the team?
            </h2>
            <p className="text-white/70 max-w-xl mx-auto mb-8">
              We're always looking for warm, dependable humans with lived experience in care. If that sounds like you, we'd love to hear from you.
            </p>
            <Link to="/contact" className="btn-gold inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold tracking-[0.12em] uppercase text-deep hover:bg-gold-light transition-all hover:scale-[1.03]" data-testid="team-join-btn">
              Get in Touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TeamPage;
